/**
 * The rest of Kimmo's room: the third bed nobody slept in, the cupboards along
 * the north wall, and the man on the floor beside the bed he never made it
 * into.
 *
 * Dressing, like the yöpöydät next door - the room is looked at from a metre
 * away on the READY screen, so it is furnished rather than blocked out.
 */

import { decor, type DecorSpec, type SprawlerSpec } from '../world/Level';
import { BED } from './levelMakuuhuone';

/* ------------------------------------------------ kolmas sänky --------- */

/**
 * The third bed, squeezed lengthwise under the name board on the north wall.
 * A pirtti rented by the mattress sleeps as many as it has walls for, and this
 * is what was left over: no yöpöytä, no south-corner symmetry, just a frame, a
 * mattress and a pillow nobody's head has touched. Its man is on the floor
 * beside it - see `TUNTEMATON` below, which is the whole of the joke.
 *
 * Decor from the frame down, unlike the other two beds, whose frames are
 * colliders. `YOPOYTA`'s exception would allow a solid here - no line through
 * the room goes near the north wall - but this bed exists to stage a gag, and
 * the pirtti's rule for gag props (`TUNTEMATON`, `AMPARI`) is the one that fits: a
 * collider added for a joke would retire every stored leaderboard time. A
 * frame you could in principle walk through is a frame nobody has a reason to
 * walk through.
 */
export const KOLMAS_SANKY = {
  /** Along the wall, centred under the name board. See `signs`. */
  fromX: 3.6,
  toX: 6.0,
  /** Flush with `pirtti-seina-pohjoinen`'s inner face - the beds' own rule. */
  backZ: 4.0,
} as const;

export function kolmasSanky(): DecorSpec[] {
  const s = KOLMAS_SANKY;
  const frontZ = s.backZ - BED.width;
  const top = BED.frameHeight;
  return [
    decor('sanky-kolmas', 'furniture', s.fromX, frontZ, s.toX, s.backZ, top),
    decor(
      'patja-kolmas',
      'fabric',
      s.fromX + 0.08,
      frontZ + 0.08,
      s.toX - 0.08,
      s.backZ - 0.08,
      BED.mattress,
      top,
    ),
    // Pillow at the west end, the same inset the other two pillows get: head
    // to the blind end of the room, feet towards the door he was aiming for.
    decor(
      'tyyny-kolmas',
      'fabric',
      s.fromX + 0.1,
      frontZ + 0.2,
      s.fromX + 0.68,
      s.backZ - 0.2,
      0.14,
      top + BED.mattress,
    ),
  ];
}

/* ---------------------------------------- pohjoisseinän kaapit --------- */

/**
 * The vaatekaappi in the room's blind corner, which the rest of the north
 * wall is now placed off.
 *
 * Its own numbers rather than four literals down in `solids`, because the two
 * cabinets that arrived beside it measure themselves against it: the lipasto
 * stands flush with its east face and the seinäkaappi tops out level with its
 * lid. Wardrobe, chest and cupboard read as one run of storage rather than as
 * three pieces of furniture that happen to share a wall - and moving the
 * wardrobe now moves the run instead of opening a slot in it, which is the
 * beds' own rule one wall round.
 */
export const VAATEKAAPPI = {
  fromX: 0.0,
  toX: 1.2,
  /** Backed onto the north wall's inner face and stood against the west one. */
  frontZ: 2.4,
  backZ: 4.0,
  height: 2.1,
} as const;

/**
 * A lipasto against the north wall, with a seinäkaappi hung over it.
 *
 * Both are decor from the carcass out, which is the third bed's rule two
 * metres along the same wall: no line through this room goes near the north
 * side, so a solid would be *allowed* - and it would retire every stored
 * leaderboard time to add a chest of drawers nobody can reach. Dressing is
 * dressing, and dressing is free. `Level.test.ts` hashes the level with the
 * whole run of them taken back out to prove it cost nothing.
 *
 * What they are here for is that a pirtti rented by the mattress still belongs
 * to somebody. Everything else in this room arrived with the lads and is on
 * the floor - the vaatekasa, the kaljakori, the man who never made it into his
 * own bed - and a room furnished entirely out of a car boot reads as a set
 * rather than as somebody's house. The lipasto was here before any of them
 * and will be here after, and the one thing about it that is theirs is the
 * sleeve shut in the second drawer on the way out.
 *
 * The fronts are the whole of why this is a lipasto and not a cupboard with
 * lines drawn on it, so they are authored as weights rather than heights: four
 * of them, each a little deeper than the one above, dividing whatever the
 * sokkeli and the kansi leave of the carcass between them. That is how a chest
 * of drawers is actually built, and at the distance this room is looked at it
 * is the only thing that says so.
 */
export const LIPASTO = {
  /** Flush with the vaatekaappi. See `VAATEKAAPPI` for why there is no gap. */
  fromX: VAATEKAAPPI.toX,
  width: 1.12,
  depth: 0.46,
  height: 0.86,
  /** The sokkeli, set back so the carcass stands over it rather than on it. */
  plinthHeight: 0.09,
  plinthInset: 0.045,
  /** The kansi. Proud at the front and at the open end, flush at the wardrobe. */
  lidThickness: 0.03,
  lidOverhang: 0.022,
  /**
   * Deepest at the floor and shallowest under the lid, read bottom up: the
   * first entry is the drawer standing on the sokkeli.
   */
  drawers: [1.12, 1.06, 0.96, 0.86],
  /** The shadow line round every front. */
  gap: 0.012,
  /**
   * How far a front stands off the face behind it - and the unit every layer
   * of this thing is stacked in. Each panel is a slab of `2 * proud` centred
   * on the face it is fitted to, so half of it is buried and no two boxes here
   * ever share a plane: a vedin is that same slab one layer further out. The
   * coplanar pass would carry a tie (see `src/render/coplanar.ts`), but a
   * drawer front is a thing with a thickness rather than a decal, and building
   * it that way is what gives the row its shadow.
   */
  proud: 0.016,
  /** The vedin across the middle of each front. */
  handleWidth: 0.34,
  handleHeight: 0.022,
  /**
   * Which drawer got shut on a sleeve, and how far it hangs down. Past the
   * bottom of that front and onto the one below, because a patch that stops
   * at the drawer it came out of reads as something stuck to the furniture
   * rather than as cloth hanging off it. Off to the east end rather than in
   * the middle, because the vedin is in the middle - and because a shirt
   * caught dead centre reads as an arrangement rather than as an accident.
   */
  sleeve: { drawer: 2, drop: 0.26, width: 0.13, inset: 0.14 },
} as const;

/**
 * Hung over the lipasto and no wider than it, so the two of them read as one
 * piece of furniture rather than as a shelf that landed above a chest.
 *
 * Shallower than the lipasto, the way a wall cupboard is: nobody wants a metre
 * of cabinet over their head, and the step back from the chest below it is
 * what makes the wall read as depth rather than as one flat slab of `wood`.
 * The top is the vaatekaappi's own - the wall already has that line, and the
 * board over the third bed hangs its bottom edge on it too.
 */
const SEINAKAAPPI = {
  depth: 0.35,
  bottom: 1.35,
  top: VAATEKAAPPI.height,
  /** Margin round the pair of leaves, and the shadow line between them. */
  margin: 0.022,
  gap: 0.012,
  knob: 0.042,
} as const;

/** The lipasto: sokkeli, carcass, kansi, four fronts and their vetimet. */
export function lipasto(): DecorSpec[] {
  const l = LIPASTO;
  const toX = l.fromX + l.width;
  const frontZ = VAATEKAAPPI.backZ - l.depth;
  const lidY = l.height - l.lidThickness;
  const centerX = l.fromX + l.width / 2;

  const items: DecorSpec[] = [
    decor(
      'lipasto-sokkeli',
      'wood',
      l.fromX + l.plinthInset,
      frontZ + l.plinthInset,
      toX - l.plinthInset,
      VAATEKAAPPI.backZ,
      l.plinthHeight,
    ),
    decor(
      'lipasto-runko',
      'furniture',
      l.fromX,
      frontZ,
      toX,
      VAATEKAAPPI.backZ,
      lidY - l.plinthHeight,
      l.plinthHeight,
    ),
    // Flush against the vaatekaappi and proud everywhere else: a lid that
    // overhung on that side would be a lid growing into a wardrobe.
    decor(
      'lipasto-kansi',
      'furniture',
      l.fromX,
      frontZ - l.lidOverhang,
      toX + l.lidOverhang,
      VAATEKAAPPI.backZ,
      l.lidThickness,
      lidY,
    ),
  ];

  // The fronts divide what the sokkeli and the kansi leave, with the same
  // shadow line above, below and between them - so `drawers` can be re-weighted
  // without any of the arithmetic below having to be redone.
  const inner = lidY - l.plinthHeight - (l.drawers.length + 1) * l.gap;
  const unit = inner / l.drawers.reduce((sum, weight) => sum + weight, 0);
  let y = l.plinthHeight + l.gap;
  let sleeveTop = 0;

  l.drawers.forEach((weight, index) => {
    // Deepest at the floor: the array is read from the bottom up.
    const n = l.drawers.length - index;
    const height = unit * weight;
    items.push(
      decor(
        `lipasto-laatikko-${n}`,
        'wood',
        l.fromX + l.gap,
        frontZ - l.proud,
        toX - l.gap,
        frontZ + l.proud,
        height,
        y,
      ),
      decor(
        `lipasto-vedin-${n}`,
        'metal',
        centerX - l.handleWidth / 2,
        frontZ - 2 * l.proud,
        centerX + l.handleWidth / 2,
        frontZ,
        l.handleHeight,
        y + (height - l.handleHeight) / 2,
      ),
    );
    if (n === l.sleeve.drawer) sleeveTop = y + height;
    y += height + l.gap;
  });

  // What somebody was looking for at six in the morning, still hanging out of
  // the drawer he found it in. Caught in the seam above the front rather than
  // laid over the lid, because a drawer shut on a shirt is the whole sentence
  // and a shirt on top of a chest is not a sentence at all.
  const s = l.sleeve;
  items.push(
    decor(
      'lipasto-hiha',
      'fabric',
      toX - s.inset - s.width,
      frontZ - 2 * l.proud,
      toX - s.inset,
      frontZ,
      s.drop,
      sleeveTop + l.gap - s.drop,
    ),
  );

  return items;
}

/** The seinäkaappi: carcass, two leaves and the knob on each. */
export function seinakaappi(): DecorSpec[] {
  const l = LIPASTO;
  const s = SEINAKAAPPI;
  const toX = l.fromX + l.width;
  const frontZ = VAATEKAAPPI.backZ - s.depth;
  const height = s.top - s.bottom;
  // The leaves meet in the middle of the carcass, half the shadow line each.
  const midX = l.fromX + l.width / 2;
  const knobY = s.bottom + (height - s.knob) / 2;

  const leaf = (suffix: string, fromX: number, leafToX: number): DecorSpec =>
    decor(
      `seinakaappi-ovi-${suffix}`,
      'wood',
      fromX,
      frontZ - l.proud,
      leafToX,
      frontZ + l.proud,
      height - 2 * s.margin,
      s.bottom + s.margin,
    );

  // Both nupit at the leading edge of their own leaf, i.e. either side of the
  // shadow line down the middle. That is the one detail that says which way a
  // pair of doors opens.
  const knob = (suffix: string, fromX: number): DecorSpec =>
    decor(
      `seinakaappi-nuppi-${suffix}`,
      'metal',
      fromX,
      frontZ - 2 * l.proud,
      fromX + s.knob,
      frontZ,
      s.knob,
      knobY,
    );

  return [
    decor(
      'seinakaappi-runko',
      'furniture',
      l.fromX,
      frontZ,
      toX,
      VAATEKAAPPI.backZ,
      height,
      s.bottom,
    ),
    leaf('lansi', l.fromX + s.margin, midX - s.gap / 2),
    leaf('ita', midX + s.gap / 2, toX - s.margin),
    knob('lansi', midX - s.gap / 2 - s.margin - s.knob),
    knob('ita', midX + s.gap / 2 + s.margin),
  ];
}

/* -------------------------------------------- tuntematon lattialla ---- */

/**
 * The man who has a bed of his own and still did not make it into it, and
 * whose name nobody in this room can tell you.
 *
 * The third man in the room: face down on the floorboards beside the third
 * bed, head pointed at the door, trousers round his knees and the rest of him
 * still in the air. He stopped part-way through whatever he was doing, which
 * is the whole of the joke - and with the made, untouched bed a half step
 * behind him, the READY screen now shows what he failed at as well as how.
 *
 * The bed behind him used to carry a lappu with his koodinimi on it, and
 * taking that lappu off is what finished the gag rather than what spoiled it.
 * Kimmo's bed and Juppe's are labelled, so the room's own rule is established
 * before you look at this one; the one bed that is not labelled is therefore a
 * bed whose owner nobody could name at the point the tape went up. He is the
 * only man in the pihapiiri who does not get a name, and he is also the only
 * one lying like this - see `NIMILAPPU` for the other half of it.
 *
 * He is dressing rather than a body to be hurdled, and that is the same
 * decision the ampari by the bed is: nothing within reach of the spawn is a
 * collider here, because a man laid across the door side of the floor would be
 * a difficulty change in the one second of the run where the meter has nothing
 * on it yet - and it would retire every stored leaderboard time to add a gag.
 * So `sprawlers` is drawn and never simulated. See `SprawlerSpec`.
 *
 * Placed by his hips, because they are the one part of him that is off the
 * floor, and from there he reaches about a metre each way. That is what the
 * two numbers are picked for: parallel to his own bed and half a step short of
 * its rail, clear of the kaljakori a stride to the south, and his hands stop
 * outside the open bedroom arch and its running approach.
 */
export const TUNTEMATON: SprawlerSpec = {
  name: 'tuntematon-lattialla',
  hips: [5.1, 2.35],
  /** Head towards the door, which from where he is lying is straight down +x. */
  rotationY: Math.PI / 2,
  // A grown man, like everybody else in the pihapiiri: the average age in this
  // hall is the wrong side of thirty and the bodies are drawn accordingly.
  height: 1.78,
};
